import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  type EnablePaymentMethodInput,
  type SavePaymentMethodInput,
  type SavePlatformBillingInput,
  type StartUpgradeInput,
  type AssignPlanInput,
  type UpdatePlanInput,
  type CouponDto,
  type CreateCouponInput,
  type CreateProductInput,
  type CustomerDto,
  type DashboardMetricsDto,
  type OrderDto,
  type OrderSummaryDto,
  type ProductDto,
  type ReportPeriod,
  type SalesPageContent,
  type SalesPageDto,
  type SalesPageWithContentDto,
  type UpdateCouponInput,
  type UpdateProductInput,
} from '@musleempay/shared';
import { type ApiResult } from '../lib/api-client.js';
import {
  adminService,
  categoryService,
  couponService,
  customerService,
  merchantService,
  orderService,
  productService,
  reportService,
  salesPageService,
} from '../services/index.js';

/**
 * Data-access hooks.
 *
 * Components get loading/error/data from these and render; they contain no
 * fetching logic of their own. Query keys are centralised in `queryKeys` so
 * invalidation after a mutation cannot drift out of sync with the reads.
 */

export const queryKeys = {
  session: ['session'] as const,
  onboarding: ['onboarding'] as const,
  settings: ['settings'] as const,
  staff: ['staff'] as const,
  invitations: ['staff', 'invitations'] as const,
  shipping: ['shipping'] as const,
  adminTeam: ['admin', 'team'] as const,
  adminStores: (query: Record<string, unknown>) => ['admin', 'stores', query] as const,
  adminStore: (id: string) => ['admin', 'stores', id] as const,
  adminActivity: (query: Record<string, unknown>) => ['admin', 'activity', query] as const,
  adminAccessLog: (query: Record<string, unknown>) => ['admin', 'access-log', query] as const,
  adminOverview: (query: Record<string, unknown>) => ['admin', 'overview', query] as const,
  categories: ['categories'] as const,
  subscription: ['subscription'] as const,
  plans: ['plans'] as const,
  adminPlans: ['admin', 'plans'] as const,
  paymentMethods: ['payment-methods'] as const,
  billingOrders: ['billing', 'orders'] as const,
  billingOrder: (id: string) => ['billing', 'orders', id] as const,
  adminBillingSettings: ['admin', 'billing', 'settings'] as const,
  adminBillingOrders: (query: Record<string, unknown>) => ['admin', 'billing', 'orders', query] as const,
  adminStoreSubscription: (id: string) => ['admin', 'stores', id, 'subscription'] as const,

  products: (query: Record<string, unknown>) => ['products', query] as const,
  product: (id: string) => ['products', id] as const,

  salesPages: (query: Record<string, unknown>) => ['sales-pages', query] as const,
  salesPage: (id: string) => ['sales-pages', id] as const,

  orders: (query: Record<string, unknown>) => ['orders', query] as const,
  order: (id: string) => ['orders', id] as const,

  customers: (query: Record<string, unknown>) => ['customers', query] as const,
  customer: (id: string) => ['customers', id] as const,
  customerOrders: (id: string) => ['customers', id, 'orders'] as const,
  customerNotes: (id: string) => ['customers', id, 'notes'] as const,

  coupons: (query: Record<string, unknown>) => ['coupons', query] as const,
  coupon: (id: string) => ['coupons', id] as const,

  dashboard: (query: Record<string, unknown>) => ['reports', 'dashboard', query] as const,
};

type ListQuery = Record<string, string | number | boolean | undefined | null>;

export function useCreateCategory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => categoryService.create({ name }),
    // Returned so callers can select the new category once the list has it.
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.categories }),
  });
}

// -----------------------------------------------------------------------------
// Products
// -----------------------------------------------------------------------------

export function useProducts(query: ListQuery): UseQueryResult<ApiResult<ProductDto[]>> {
  return useQuery({
    queryKey: queryKeys.products(query),
    queryFn: ({ signal }) => productService.list(query, signal),
    // Keeps the previous page visible while the next one loads, so the table
    // does not collapse to a spinner on every page change.
    placeholderData: (previous) => previous,
  });
}

export function useProduct(id: string | undefined): UseQueryResult<ProductDto> {
  return useQuery({
    queryKey: queryKeys.product(id ?? ''),
    queryFn: ({ signal }) => productService.get(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useCreateProduct(): UseMutationResult<ProductDto, Error, CreateProductInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => productService.create(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['products'] });
      // Creating the first product can complete an onboarding step.
      void client.invalidateQueries({ queryKey: queryKeys.onboarding });
    },
  });
}

export function useUpdateProduct(
  id: string,
): UseMutationResult<ProductDto, Error, UpdateProductInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProductInput) => productService.update(id, input),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.product(id), updated);
      void client.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteProduct(): UseMutationResult<{ ok: true }, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productService.remove(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: () => categoryService.list(),
    // Categories change rarely; avoid refetching them on every screen.
    staleTime: 5 * 60_000,
  });
}

// -----------------------------------------------------------------------------
// Sales pages
// -----------------------------------------------------------------------------

export function useSalesPages(query: ListQuery): UseQueryResult<ApiResult<SalesPageDto[]>> {
  return useQuery({
    queryKey: queryKeys.salesPages(query),
    queryFn: ({ signal }) => salesPageService.list(query, signal),
    placeholderData: (previous) => previous,
  });
}

export function useSalesPage(id: string | undefined): UseQueryResult<SalesPageWithContentDto> {
  return useQuery({
    queryKey: queryKeys.salesPage(id ?? ''),
    queryFn: ({ signal }) => salesPageService.get(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useSaveSalesPageContent(
  id: string,
): UseMutationResult<SalesPageWithContentDto, Error, { content: SalesPageContent; changeNote?: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ content, changeNote }) => salesPageService.saveContent(id, content, changeNote),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.salesPage(id), updated);
    },
  });
}

export function usePublishSalesPage(): UseMutationResult<
  SalesPageDto,
  Error,
  { id: string; publish: boolean }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, publish }) =>
      publish ? salesPageService.publish(id) : salesPageService.unpublish(id),
    onSuccess: (_updated, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.salesPage(variables.id) });
      void client.invalidateQueries({ queryKey: ['sales-pages'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Orders
// -----------------------------------------------------------------------------

export function useOrders(query: ListQuery): UseQueryResult<ApiResult<OrderSummaryDto[]>> {
  return useQuery({
    queryKey: queryKeys.orders(query),
    queryFn: ({ signal }) => orderService.list(query, signal),
    placeholderData: (previous) => previous,
  });
}

export function useOrder(id: string | undefined): UseQueryResult<OrderDto> {
  return useQuery({
    queryKey: queryKeys.order(id ?? ''),
    queryFn: ({ signal }) => orderService.get(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useUpdateOrderStatus(
  id: string,
): UseMutationResult<OrderDto, Error, { status: string; note?: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ status, note }) => orderService.setStatus(id, status, note),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.order(id), updated);
      void client.invalidateQueries({ queryKey: ['orders'] });
      // Status changes move money, so the dashboard figures are now stale.
      void client.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useRefundOrder(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { amount?: string; reason: string }) => orderService.refund(id, input),
    onSuccess: (result) => {
      client.setQueryData(queryKeys.order(id), result.order);
      void client.invalidateQueries({ queryKey: ['orders'] });
      void client.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useCreateShipment(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      courierName: string;
      trackingNumber: string;
      trackingUrl?: string | null;
      note?: string;
    }) => orderService.createShipment(id, input),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.order(id), updated);
      void client.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Customers
// -----------------------------------------------------------------------------

export function useCustomers(query: ListQuery): UseQueryResult<ApiResult<CustomerDto[]>> {
  return useQuery({
    queryKey: queryKeys.customers(query),
    queryFn: ({ signal }) => customerService.list(query, signal),
    placeholderData: (previous) => previous,
  });
}

export function useCustomer(id: string | undefined): UseQueryResult<CustomerDto> {
  return useQuery({
    queryKey: queryKeys.customer(id ?? ''),
    queryFn: ({ signal }) => customerService.get(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useCustomerOrders(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customerOrders(id ?? ''),
    queryFn: () => customerService.orders(id as string),
    enabled: Boolean(id),
  });
}

export function useCustomerNotes(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customerNotes(id ?? ''),
    queryFn: () => customerService.notes(id as string),
    enabled: Boolean(id),
  });
}

export function useAddCustomerNote(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => customerService.addNote(id, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.customerNotes(id) });
    },
  });
}

// -----------------------------------------------------------------------------
// Coupons
// -----------------------------------------------------------------------------

export function useCoupons(query: ListQuery): UseQueryResult<ApiResult<CouponDto[]>> {
  return useQuery({
    queryKey: queryKeys.coupons(query),
    queryFn: ({ signal }) => couponService.list(query, signal),
    placeholderData: (previous) => previous,
  });
}

export function useCreateCoupon(): UseMutationResult<CouponDto, Error, CreateCouponInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCouponInput) => couponService.create(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['coupons'] });
    },
  });
}

export function useUpdateCoupon(
  id: string,
): UseMutationResult<CouponDto, Error, UpdateCouponInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCouponInput) => couponService.update(id, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['coupons'] });
    },
  });
}

export function useDeleteCoupon() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => couponService.remove(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['coupons'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Reports, settings, staff
// -----------------------------------------------------------------------------

export function useDashboardMetrics(options: {
  period: ReportPeriod;
  from?: string;
  to?: string;
}): UseQueryResult<DashboardMetricsDto> {
  const query: ListQuery = {
    period: options.period,
    ...(options.from ? { from: options.from } : {}),
    ...(options.to ? { to: options.to } : {}),
    compare: true,
  };

  return useQuery({
    queryKey: queryKeys.dashboard(query),
    queryFn: ({ signal }) => reportService.dashboard(query, signal),
    // Figures move as orders arrive, but not second by second.
    staleTime: 60_000,
  });
}

export function useMerchantSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => merchantService.settings(),
  });
}

export function useUpdateMerchantSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof merchantService.updateSettings>[0]) =>
      merchantService.updateSettings(input),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.settings, updated);
      // The store name and slug appear in the shell, which reads the session.
      void client.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useStaff() {
  return useQuery({ queryKey: queryKeys.staff, queryFn: () => merchantService.staff() });
}

export function useStaffInvitations() {
  return useQuery({
    queryKey: queryKeys.invitations,
    queryFn: () => merchantService.invitations(),
  });
}

export function useInviteStaff() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof merchantService.inviteStaff>[0]) =>
      merchantService.inviteStaff(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.invitations });
    },
  });
}

export function useShippingZones() {
  return useQuery({
    queryKey: queryKeys.shipping,
    queryFn: () => merchantService.shippingZones(),
  });
}

export function useCreateShippingZone() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof merchantService.createShippingZone>[0]) =>
      merchantService.createShippingZone(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.shipping });
    },
  });
}

export function useUpdateShippingZone() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; data: Parameters<typeof merchantService.updateShippingZone>[1] }) =>
      merchantService.updateShippingZone(input.id, input.data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.shipping });
    },
  });
}

export function useDeleteShippingZone() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => merchantService.deleteShippingZone(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.shipping });
    },
  });
}

export function useCreateShippingMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof merchantService.createShippingMethod>[0]) =>
      merchantService.createShippingMethod(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.shipping });
    },
  });
}

export function useUpdateShippingMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; data: Parameters<typeof merchantService.updateShippingMethod>[1] }) =>
      merchantService.updateShippingMethod(input.id, input.data),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.shipping });
    },
  });
}

export function useDeleteShippingMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => merchantService.deleteShippingMethod(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.shipping });
    },
  });
}

export function useSetStaffOrderEmails() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; receivesOrderEmails: boolean }) =>
      merchantService.setStaffOrderEmails(input.id, input.receivesOrderEmails),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.staff });
    },
  });
}

export function useAdminTeam() {
  return useQuery({
    queryKey: queryKeys.adminTeam,
    queryFn: () => adminService.team(),
  });
}

export function useSetPlatformUserCc() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; ccOrderEmails: boolean }) =>
      adminService.setCcOrderEmails(input.id, input.ccOrderEmails),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.adminTeam });
    },
  });
}

export function useAdminOverview(query: ListQuery) {
  return useQuery({
    queryKey: queryKeys.adminOverview(query),
    queryFn: ({ signal }) => adminService.overview(query, signal),
  });
}

export function useAdminStores(query: ListQuery) {
  return useQuery({
    queryKey: queryKeys.adminStores(query),
    queryFn: ({ signal }) => adminService.stores(query, signal),
    // Keep the previous page on screen while the next loads, so paging and
    // filtering don't flash an empty table.
    placeholderData: (previous) => previous,
  });
}

export function useAdminStore(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminStore(id ?? ''),
    queryFn: ({ signal }) => adminService.store(id as string, signal),
    enabled: Boolean(id),
  });
}

export function useSetStoreStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: 'ACTIVE' | 'SUSPENDED'; reason?: string }) =>
      adminService.setStoreStatus(input.id, input.status, input.reason),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.adminStore(updated.id), updated);
      // The list shows status, and the overview shows it per store.
      void client.invalidateQueries({ queryKey: ['admin', 'stores'] });
      void client.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

export function useActivateStoreUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { storeId: string; userId: string }) =>
      adminService.activateStoreUser(input.storeId, input.userId),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.adminStore(updated.id), updated);
    },
  });
}

export function useAdminActivity(query: ListQuery) {
  return useQuery({
    queryKey: queryKeys.adminActivity(query),
    queryFn: ({ signal }) => adminService.activity(query, signal),
    placeholderData: (previous) => previous,
  });
}

export function useAdminAccessLog(query: ListQuery) {
  return useQuery({
    queryKey: queryKeys.adminAccessLog(query),
    queryFn: ({ signal }) => adminService.accessLog(query, signal),
    placeholderData: (previous) => previous,
    // A visit that is open right now should close on screen without a manual
    // refresh; this is a short, cheap list read by one or two people.
    refetchInterval: 30_000,
  });
}

export function useStartSupportAccess() {
  return useMutation({
    mutationFn: (input: { merchantId: string; reason: string }) =>
      adminService.startSupportAccess(input.merchantId, input.reason),
  });
}

export function useOnboardingState() {
  return useQuery({
    queryKey: queryKeys.onboarding,
    queryFn: () => merchantService.onboarding(),
  });
}

export function useCompleteOnboardingStep() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { step: Parameters<typeof merchantService.completeOnboardingStep>[0]; data: unknown }) =>
      merchantService.completeOnboardingStep(input.step, input.data),
    // Returned, so the mutation only settles once both are fresh. The step that
    // finishes setup navigates straight to the dashboard, whose route guard reads
    // the session: with a stale one it would send the person back to onboarding.
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.onboarding }),
        client.invalidateQueries({ queryKey: queryKeys.session }),
      ]),
  });
}

// -----------------------------------------------------------------------------
// Plans and subscription
// -----------------------------------------------------------------------------

export function useSubscription() {
  return useQuery({
    queryKey: queryKeys.subscription,
    queryFn: () => merchantService.subscription(),
    // The trial countdown only changes by the day.
    staleTime: 60_000,
  });
}

export function usePlans() {
  return useQuery({ queryKey: queryKeys.plans, queryFn: () => merchantService.plans() });
}

export function useAdminPlans() {
  return useQuery({ queryKey: queryKeys.adminPlans, queryFn: () => adminService.plans() });
}

export function useUpdatePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; changes: UpdatePlanInput }) =>
      adminService.updatePlan(input.id, input.changes),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.adminPlans });
      void client.invalidateQueries({ queryKey: queryKeys.plans });
    },
  });
}

export function useAdminStoreSubscription(storeId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminStoreSubscription(storeId ?? ''),
    queryFn: () => adminService.storeSubscription(storeId as string),
    enabled: Boolean(storeId),
  });
}

export function useAssignStorePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { storeId: string; plan: AssignPlanInput }) =>
      adminService.assignStorePlan(input.storeId, input.plan),
    onSuccess: (_updated, input) => {
      void client.invalidateQueries({
        queryKey: queryKeys.adminStoreSubscription(input.storeId),
      });
      void client.invalidateQueries({ queryKey: queryKeys.adminPlans });
    },
  });
}

// -----------------------------------------------------------------------------
// Upgrading a plan
// -----------------------------------------------------------------------------

export function useBillingStatus() {
  return useQuery({
    queryKey: ['billing', 'status'] as const,
    queryFn: () => merchantService.billingStatus(),
    staleTime: 60_000,
  });
}

export function useStartUpgrade() {
  return useMutation({ mutationFn: (input: StartUpgradeInput) => merchantService.startUpgrade(input) });
}

export function useBillingOrders() {
  return useQuery({ queryKey: queryKeys.billingOrders, queryFn: () => merchantService.billingOrders() });
}

/** One order, polled while it is still waiting to be paid. */
export function useBillingOrder(id: string | null) {
  const client = useQueryClient();
  return useQuery({
    queryKey: queryKeys.billingOrder(id ?? ''),
    queryFn: async () => {
      const order = await merchantService.billingOrder(id as string);
      if (order.status === 'PAID') {
        // The plan just changed: everything that shows it must catch up.
        void client.invalidateQueries({ queryKey: queryKeys.subscription });
        void client.invalidateQueries({ queryKey: queryKeys.billingOrders });
        void client.invalidateQueries({ queryKey: queryKeys.paymentMethods });
      }
      return order;
    },
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data?.status === 'PENDING' ? 4_000 : false),
  });
}

// -----------------------------------------------------------------------------
// Payment methods
// -----------------------------------------------------------------------------

export function usePaymentMethods() {
  return useQuery({
    queryKey: queryKeys.paymentMethods,
    queryFn: () => merchantService.paymentMethods(),
  });
}

export function useSavePaymentMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; data: SavePaymentMethodInput }) =>
      merchantService.savePaymentMethod(input.code, input.data),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.paymentMethods }),
  });
}

export function useTestPaymentMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => merchantService.testPaymentMethod(code),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.paymentMethods }),
  });
}

export function useEnablePaymentMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; data: EnablePaymentMethodInput }) =>
      merchantService.enablePaymentMethod(input.code, input.data),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.paymentMethods }),
  });
}

export function useRemovePaymentMethod() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => merchantService.removePaymentMethod(code),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.paymentMethods }),
  });
}

export function useMarkOrderPaid(orderId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => orderService.markPaid(orderId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.order(orderId) });
      void client.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Platform billing (super admin)
// -----------------------------------------------------------------------------

export function useAdminBillingSettings() {
  return useQuery({
    queryKey: queryKeys.adminBillingSettings,
    queryFn: () => adminService.billingSettings(),
  });
}

export function useSaveAdminBillingSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SavePlatformBillingInput) => adminService.saveBillingSettings(input),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.adminBillingSettings }),
  });
}

export function useTestAdminBilling() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => adminService.testBilling(),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.adminBillingSettings }),
  });
}

export function useAdminBillingOrders(query: ListQuery) {
  return useQuery({
    queryKey: queryKeys.adminBillingOrders(query),
    queryFn: () => adminService.billingOrders(query),
  });
}
